import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';

import SplashScreen from './components/SplashScreen';
import Navbar from './components/Navbar';
import ParticleBackground from './components/ParticleBackground';
import BookingFlow from './pages/Booking/BookingFlow';
import BookingConfirmation from './pages/Booking/BookingConfirmation';
import MyAppointment from './pages/MyAppointment';
import AdminLogin from './pages/Admin/Login';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminAppointments from './pages/Admin/Appointments';
import AdminServices from './pages/Admin/Services';
import AdminTimeSlots from './pages/Admin/TimeSlots';
import AdminClients from './pages/Admin/Clients';
import AdminClientDetail from './pages/Admin/ClientDetail';
import AdminImport from './pages/Admin/Import';
import AdminAnalytics from './pages/Admin/Analytics';
import AdminSettings from './pages/Admin/Settings';
import AdminCRM from './pages/Admin/CRM';
import ProfileEditor from './pages/Admin/ProfileEditor';
import AdminWaitlist from './pages/Admin/Waitlist';
import AdminPackages from './pages/Admin/Packages';
import AdminAddons from './pages/Admin/Addons';
import AdminReferrals from './pages/Admin/Referrals';
import AdminGiftCards from './pages/Admin/GiftCards';
import AdminTaxSummary from './pages/Admin/TaxSummary';
import ProfessionalDashboard from './pages/Professional/Dashboard';
import SocialPosting from './pages/Professional/SocialPosting';
import Join from './pages/Join';
import BarbersLanding from './pages/BarbersLanding';
import BarberProfile from './pages/BarberProfile';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Founding, { FoundingSuccess } from './pages/Founding';
import CancelAppointment from './pages/CancelAppointment';
import ClientPortal from './pages/ClientPortal';
import Find from './pages/Find';
import ProfessionLanding from './pages/ProfessionLanding';
import VsBooksy from './pages/VsBooksy';
import VsVagaro from './pages/VsVagaro';
import VsSquare from './pages/VsSquare';
import VsGlossGenius from './pages/VsGlossGenius';
import VsFresha from './pages/VsFresha';
import Story from './pages/Story';
import ProSignup from './pages/ProSignup';
import GiftCardStore from './pages/GiftCardStore';
import JoinWaitlist from './pages/JoinWaitlist';

// /book → /christiangflair (preserves query params like ?service=1)
function BookRedirect() {
  const location = useLocation();
  return <Navigate to={`/christiangflair${location.search}`} replace />;
}

// Reads the pro's configured slug from the API and redirects to /:slug
function BarberRedirect() {
  const [slug, setSlug] = useState(null);
  const location = useLocation();
  useEffect(() => {
    fetch('/api/settings/public')
      .then(r => r.json())
      .then(data => {
        if (data.profile) {
          try { setSlug(JSON.parse(data.profile).businessSlug || null); } catch {}
        }
      }).catch(() => {});
  }, []);
  if (!slug) return null;
  // Preserve query params (e.g. ?service=1&ref=CODE)
  return <Navigate to={`/${slug}${location.search}`} replace />;
}

function ProtectedRoute({ children }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/dash/login" replace />;
}

// Pages that manage their own full-page layout (no global Navbar)
const FULL_PAGE_ROUTES = ['/', '/barbers', '/professionals', '/nail-techs', '/stylists', '/estheticians', '/coaches', '/founding', '/founding/success', '/join', '/signup', '/barber', '/pro', '/dash', '/pricing', '/about', '/client', '/find', '/gift', '/join-waitlist'];

function SmartNavbar() {
  const location = useLocation();
  const hideNav = FULL_PAGE_ROUTES.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
    || /^\/[^/]+$/.test(location.pathname); // single-segment slug routes are full-page
  if (hideNav) return null;
  return <Navbar />;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-transition">
      <Routes location={location}>
        <Route path="/" element={<BarbersLanding />} />
        <Route path="/join" element={<Join />} />
        <Route path="/barbers" element={<ProfessionLanding profession="barbers" />} />
        <Route path="/professionals" element={<BarbersLanding />} />
        <Route path="/nail-techs" element={<ProfessionLanding profession="nail-techs" />} />
        <Route path="/stylists" element={<ProfessionLanding profession="stylists" />} />
        <Route path="/estheticians" element={<ProfessionLanding profession="estheticians" />} />
        <Route path="/coaches" element={<ProfessionLanding profession="coaches" />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/founding/success" element={<FoundingSuccess />} />
        <Route path="/founding" element={<Founding />} />
        <Route path="/vs-booksy" element={<VsBooksy />} />
        <Route path="/vs-vagaro" element={<VsVagaro />} />
        <Route path="/vs-square" element={<VsSquare />} />
        <Route path="/vs-glossgenius" element={<VsGlossGenius />} />
        <Route path="/vs-fresha" element={<VsFresha />} />
        <Route path="/signup" element={<ProSignup />} />
        <Route path="/story" element={<Story />} />
        <Route path="/barber" element={<BarberRedirect />} />
        <Route path="/gift" element={<GiftCardStore />} />
        <Route path="/join-waitlist" element={<JoinWaitlist />} />
        <Route path="/book" element={<BookingFlow />} />
        <Route path="/booking/confirmation" element={<BookingConfirmation />} />
        <Route path="/my-appointment" element={<MyAppointment />} />
        <Route path="/cancel/:token" element={<CancelAppointment />} />
        <Route path="/client" element={<ClientPortal />} />
        <Route path="/client/:token" element={<ClientPortal />} />
        <Route path="/find" element={<Find />} />
        <Route path="/:slug" element={<BarberProfile />} />
        <Route path="/dash/login" element={<AdminLogin />} />
        <Route path="/dash" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/dash/appointments" element={<ProtectedRoute><AdminAppointments /></ProtectedRoute>} />
        <Route path="/dash/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/dash/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
        <Route path="/dash/time-slots" element={<ProtectedRoute><AdminTimeSlots /></ProtectedRoute>} />
        <Route path="/dash/clients" element={<ProtectedRoute><AdminClients /></ProtectedRoute>} />
        <Route path="/dash/clients/:id" element={<ProtectedRoute><AdminClientDetail /></ProtectedRoute>} />
        <Route path="/dash/import" element={<ProtectedRoute><AdminImport /></ProtectedRoute>} />
        <Route path="/dash/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        <Route path="/dash/crm" element={<ProtectedRoute><AdminCRM /></ProtectedRoute>} />
        <Route path="/dash/profile" element={<ProtectedRoute><ProfileEditor /></ProtectedRoute>} />
        <Route path="/dash/waitlist" element={<ProtectedRoute><AdminWaitlist /></ProtectedRoute>} />
        <Route path="/dash/packages" element={<ProtectedRoute><AdminPackages /></ProtectedRoute>} />
        <Route path="/dash/addons" element={<ProtectedRoute><AdminAddons /></ProtectedRoute>} />
        <Route path="/dash/referrals" element={<ProtectedRoute><AdminReferrals /></ProtectedRoute>} />
        <Route path="/dash/gift-cards" element={<ProtectedRoute><AdminGiftCards /></ProtectedRoute>} />
        <Route path="/dash/tax" element={<ProtectedRoute><AdminTaxSummary /></ProtectedRoute>} />
        <Route path="/pro" element={<ProtectedRoute><ProfessionalDashboard /></ProtectedRoute>} />
        <Route path="/pro/social" element={<ProtectedRoute><SocialPosting /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(
    () => !sessionStorage.getItem('splash_shown')
  );

  const handleSplashComplete = () => {
    sessionStorage.setItem('splash_shown', '1');
    setShowSplash(false);
  };

  return (
    <HelmetProvider>
      <AuthProvider>
        <ParticleBackground />
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <BrowserRouter>
          <SmartNavbar />
          <AnimatedRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
